import React from "react";
import Modal from "react-modal";
import { Typography, Box, Avatar } from "@mui/material";
import { primary } from "../../../common/theme";

// Define the member type
export interface Member {
  name: string;
  image: string;
  position: string;
  bio?: string;
  quote?: string;
}

interface MemberModalProps {
  isOpen: boolean;
  member: Member | null;
  onClose: () => void;
}

const MemberModal: React.FC<MemberModalProps> = ({ isOpen, member, onClose }) => {
  if (!member) return null;
  
  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      contentLabel={`${member.name} Details`}
      className="modal-content--2"
      overlayClassName="modal-overlay--2"
      ariaHideApp={false}
    >
      <Box sx={{ 
        display: 'flex',
        flexDirection: 'column',
        padding: '40px',
        maxWidth: '1000px',
        margin: '0 auto',
        position: 'relative'
      }}>
        <button 
          className="closeModalButton" 
          onClick={onClose}
          style={{ 
            background: 'none', 
            border: 'none', 
            fontSize: '24px', 
            cursor: 'pointer',
            color: primary,
            position: 'absolute',
            right: '20px',
            top: '20px'
          }}
        >
          ×
        </button>

        {/* Top Section: Image and Bio */}
        <Box sx={{ 
          display: 'flex',
          flexDirection: {
            xs: 'column',
            md: 'row'
          },
          gap: '50px',
          marginBottom: '30px'
        }}>
          {/* Left side - Image */}
          <Box sx={{ flexShrink: 0 }}>
            <Avatar
              src={member.image}
              sx={{ 
                height: "240px", 
                width: "240px",
                borderRadius: '50%',
                borderTopLeftRadius: 0
              }}
            />
            <hr style={{ border: `3px solid #E38A15`, margin: '35px 50px 20px 0px', maxWidth: '250px' }} />
            <Typography 
              variant="h6" 
              sx={{ 
                color: primary, 
                marginTop: '15px',
                fontWeight: 'bold',
                fontSize: '1.1rem',
                opacity: 0.65
              }}
            >
              {member.name}
            </Typography>
          </Box>

          {/* Right side - Bio */}
          <Box sx={{ flex: 1 }}>
            <Typography 
              variant="body1" 
              sx={{ 
                color: '#0F2F4B'
              }}
              dangerouslySetInnerHTML={{ __html: member.bio || '' }}
            />
          </Box>
        </Box>

        {/* Bottom Section: Quote */}
        {member.quote && (
          <Box sx={{ 
            display: 'flex', 
            gap: '10px',
            marginTop: '30px',
            width: '100%'
          }}>
            <Typography 
              variant="h1" 
              sx={{ 
                color: primary,
                fontSize: '80px',
                lineHeight: '1',
                marginBottom: '-20px',
                marginRight: '20px',
                flexShrink: 0
              }}
            >
              <svg width="104" height="90" viewBox="0 0 104 90" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M104 0V16.2085C95.5102 17.6303 89.5673 20.6161 86.1714 25.1659C82.7755 29.7156 81.0775 37.8199 81.0775 49.4787H102.727V90H61.1265V55.8768C61.1265 38.2465 64.381 24.5972 70.8898 14.9289C77.3986 4.9763 88.4354 0 104 0ZM42.8735 0V16.2085C34.3837 17.6303 28.4408 20.6161 25.0449 25.1659C21.649 29.7156 19.951 37.8199 19.951 49.4787H41.6V90H0V55.8768C0 38.2465 3.25442 24.5972 9.76326 14.9289C16.2721 4.9763 27.3088 0 42.8735 0Z" fill="#E38A15"/>
              </svg>
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                fontSize: '1.1rem',
                fontWeight: '500',
                paddingTop: '0',
                color: '#0F2F4B'
              }}
              dangerouslySetInnerHTML={{ __html: member.quote }}
            />
          </Box>
        )}
      </Box>
    </Modal>
  );
};

export default MemberModal; 